import { useState } from 'react';
import { ApiError, api } from '../lib/api';

/**
 * Parar (ou retomar) alguma coisa, com o MOTIVO escrito.
 *
 * Nasceu extraindo o formulário que já existia na tela de mídia paga, porque o
 * cabeçalho passou a precisar do mesmo comportamento para a pausa de sistema.
 * Duas cópias divergiriam na segunda semana, e a que envelhece é sempre a menos
 * usada: aqui isso significaria a parada de emergência comportando-se diferente
 * da parada de categoria, exatamente na hora do incidente.
 *
 * Três propriedades que este componente existe para manter:
 *
 * 1. **o motivo é obrigatório e fica no registro.** Parada sem motivo escrito
 *    vira uma linha na auditoria que ninguém consegue explicar depois;
 *
 * 2. **formulário inline, e nunca `confirm()` do navegador.** O diálogo nativo
 *    não aceita motivo, não valida nada e não tem como ser testado. O playbook
 *    deste projeto é explícito sobre isso;
 *
 * 3. **aceite e APLICAÇÃO são coisas diferentes, e as duas aparecem.** Um
 *    comando aceito cuja aplicação falhou não pode ser mostrado como sucesso.
 *
 * O que ele NÃO faz: pedir confirmação por número. Parar tem de ser mais fácil
 * do que começar, e atrito aqui é atrito na hora do incidente. Parar não gasta
 * nada nem envia nada.
 */
export function PausaComMotivo({
  pausado,
  commandType,
  targetRef,
  payload,
  rotuloParar,
  rotuloRetomar,
  explicacaoAoParar,
  explicacaoAoRetomar,
  idDoCampo,
  idDoBotao,
  destaque = false,
}: {
  readonly pausado: boolean;
  /** Os dois tipos de comando, na ordem: o de parar e o de retomar. */
  readonly commandType: { readonly parar: string; readonly retomar: string };
  readonly targetRef: { readonly object_type: string; readonly object_id: string };
  readonly payload?: Record<string, unknown>;
  readonly rotuloParar: string;
  readonly rotuloRetomar: string;
  readonly explicacaoAoParar: string;
  readonly explicacaoAoRetomar: string;
  /** Id do campo, porque pode haver mais de um destes na mesma tela. */
  readonly idDoCampo: string;
  /** Botão de emergência, para o cabeçalho. */
  readonly destaque?: boolean;
  /**
   * O `data-testid` do botão que abre o formulário.
   *
   * Existe porque a pausa de emergência já era ancorada por `botao-pausa` em
   * dois testes escritos antes deste componente, e um deles é o que prova que
   * ela continua no DOM a 320 px. Trocar o identificador por elegância quebraria
   * a prova de que a emergência é alcançável no celular.
   */
  readonly idDoBotao?: string;
}) {
  const [aberto, setAberto] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [resultado, setResultado] = useState<string | null>(null);

  async function enviar() {
    if (motivo.trim().length < 3) {
      setResultado('Escreva o motivo. Ele fica no registro do comando.');
      return;
    }
    setOcupado(true);
    setResultado(null);
    try {
      const r = await api.sendCommand({
        command_type: pausado ? commandType.retomar : commandType.parar,
        target_ref: targetRef,
        reason: motivo.trim(),
        ...(payload === undefined ? {} : { payload }),
      });
      setResultado(
        r.execution === null
          ? (r.message ?? 'Comando aceito. A aplicação está pendente.')
          : r.execution.summary,
      );
      setAberto(false);
      setMotivo('');
    } catch (e) {
      setResultado(e instanceof ApiError ? e.message : 'Não foi possível falar com a API.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div className={destaque ? 'relative' : 'mt-4'}>
      {aberto ? (
        <div
          className={
            destaque
              ? 'absolute right-0 top-0 z-10 w-80 rounded border border-line-strong bg-surface p-3 shadow-lg'
              : 'rounded border border-line-strong p-3'
          }
          data-testid={`pausa-formulario-${idDoCampo}`}
        >
          <label htmlFor={`motivo-${idDoCampo}`} className="block text-xs text-ink-muted">
            Motivo {pausado ? 'da retomada' : 'da parada'}
          </label>
          <input
            id={`motivo-${idDoCampo}`}
            value={motivo}
            autoComplete="off"
            onChange={(e) => setMotivo(e.target.value)}
            className="mt-1 w-full rounded border border-line px-2 py-1 text-sm"
          />
          <p className="mt-2 text-xs text-ink-muted">
            {pausado ? explicacaoAoRetomar : explicacaoAoParar}
          </p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={enviar}
              disabled={ocupado}
              data-testid={`pausa-confirmar-${idDoCampo}`}
              className="rounded border border-accent-border bg-accent-strong px-3 py-1 text-sm text-white disabled:opacity-50"
            >
              {pausado ? 'Confirmar retomada' : 'Confirmar parada'}
            </button>
            <button
              type="button"
              onClick={() => setAberto(false)}
              disabled={ocupado}
              className="rounded border border-line-strong px-3 py-1 text-sm hover:bg-surface-raised disabled:opacity-50"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => {
            setAberto(true);
            setResultado(null);
          }}
          data-testid={idDoBotao ?? `pausa-abrir-${idDoCampo}`}
          /*
            O layout entregue desenha os dois sentidos com cores OPOSTAS, e nao
            e decoracao: parar e a acao destrutiva (`.btn.danger`) e retomar e a
            construtiva (`.btn.green`). Um botao vermelho escrito "Retomar
            sistema" faria a retomada parecer perigosa justamente quando a
            pessoa precisa dela.

            O rotulo continua dizendo o que o botao faz: a cor acompanha o
            texto, nunca o substitui (handoff: "nao depender apenas de cor").
          */
          className={[
            'rounded border font-semibold focus:outline-none focus:ring-2 focus:ring-accent',
            destaque ? 'px-3 py-1.5 text-xs' : 'px-3 py-1 text-sm',
            pausado
              ? 'border-ok-border bg-ok-surface text-ok-ink'
              : 'border-danger-border bg-danger-surface text-danger-ink',
          ].join(' ')}
        >
          {pausado ? rotuloRetomar : rotuloParar}
        </button>
      )}

      {resultado === null ? null : (
        <p
          className={
            destaque
              ? 'absolute right-0 top-10 z-10 w-80 rounded border border-line bg-surface p-2 text-xs shadow-lg'
              : 'mt-2 rounded border border-line bg-surface-sunken p-3 text-sm'
          }
          data-testid={`pausa-resultado-${idDoCampo}`}
        >
          {resultado}
        </p>
      )}
    </div>
  );
}
